import React, { useState } from 'react';
import { X, Upload, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { employeesApi } from '../services/api';
import { message } from 'antd';
import * as XLSX from 'xlsx'; // ADD THIS IMPORT
import { BASE_URL as API_URL } from '../services/api';

function BulkEmployeeUploadModal({ isOpen, onClose, onUploadSuccess }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = async () => {
    try {
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`${API_URL}/employees/bulk-upload/template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_bulk_upload_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      message.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type - only .xlsx allowed
      const validExtensions = ['.xlsx'];
      const fileName = file.name.toLowerCase();
      const isValidType = validExtensions.some(ext => fileName.endsWith(ext));

      if (!isValidType) {
        message.warning('Please upload a valid Excel file (.xlsx only)');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.warning('File size must be less than 10MB');
        return;
      }

      setUploadedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx'; // Only accept .xlsx
    input.onchange = handleFileChange;
    input.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadResult(null);
  };

  const handleSave = async () => {
    if (!uploadedFile) {
      message.warning('Please upload a file first');
      return;
    }

    try {
      setUploading(true);

      // Call the backend API
      const result = await employeesApi.bulkUploadEmployees(uploadedFile);

      console.log('Upload result:', result);

      // Process the response from backend
      setUploadResult({
        success: result.failureCount === 0,
        message: result.failureCount === 0
          ? `Successfully uploaded ${result.successCount} out of ${result.totalRecords} employees`
          : `Uploaded ${result.successCount} employees, ${result.failureCount} failed`,
        totalProcessed: result.totalRecords || 0,
        successCount: result.successCount || 0,
        failureCount: result.failureCount || 0,
        failedEmployees: (result.errors || []).map(err => ({
          row: err.rowNumber?.toString() || '?',
          error: err.errorMessage || 'Unknown error'
        })),
        successfulEmployees: result.successfulEmployees || []
      });

      // Notify parent component if upload was successful
      if (result.successCount > 0 && onUploadSuccess) {
        onUploadSuccess(result);
      }

      // Auto close after 3 seconds if no errors
      if (result.failureCount === 0) {
        setTimeout(() => {
          handleClose();
        }, 3000);
      }

    } catch (error) {
      console.error('Error uploading employees:', error);
      setUploadResult({
        success: false,
        message: error.message || 'Failed to upload employees. Please try again.',
        totalProcessed: 0,
        successCount: 0,
        failureCount: 1,
        failedEmployees: [{ row: '0', error: error.message || 'Network error occurred' }]
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setUploadedFile(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header - Light mint/green */}
        <div className="bg-[#77B9A0] px-4 py-2 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-800">Add bulk Employees</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-800" />
          </button>
        </div>

        {/* Body - White */}
        <div className="p-3 space-y-2">
          {/* Upload Result Alert */}
          {uploadResult && (
            <div className={`p-4 rounded-lg border-2 ${uploadResult.success
              ? 'bg-green-50 border-green-300'
              : 'bg-red-50 border-red-300'
              }`}>
              <div className="flex items-start space-x-3">
                {uploadResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className={`font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                    {uploadResult.message}
                  </h4>

                  {uploadResult.totalProcessed > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-600 mb-1">Total Processed</p>
                        <p className="text-lg font-bold text-gray-800">{uploadResult.totalProcessed}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Successful</p>
                        <p className="text-lg font-bold text-green-600">{uploadResult.successCount}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-red-200">
                        <p className="text-xs text-gray-600 mb-1">Failed</p>
                        <p className="text-lg font-bold text-red-600">{uploadResult.failureCount}</p>
                      </div>
                    </div>
                  )}

                  {uploadResult.failedEmployees && uploadResult.failedEmployees.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-red-800 mb-2">
                        Failed Employees ({uploadResult.failedEmployees.length}):
                      </p>
                      <div className="bg-white rounded-lg border border-red-200 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-red-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-semibold text-red-800">Row</th>
                              <th className="text-left p-2 font-semibold text-red-800">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.failedEmployees.slice(0, 10).map((failed, index) => (
                              <tr key={index} className="border-t border-red-100">
                                <td className="p-2 text-gray-700">{failed.row}</td>
                                <td className="p-2 text-red-700">{failed.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {uploadResult.failedEmployees.length > 10 && (
                          <div className="p-2 text-center text-xs text-red-600 bg-red-50 border-t border-red-100">
                            ...and {uploadResult.failedEmployees.length - 10} more errors
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Section 1: Download Template */}
          <div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-[#77B9A0] text-white rounded-full flex items-center justify-center font-bold text-xs">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-1">
                  Please download Excel file template.
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Download the .xlsx template and fill in your employee data.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center space-x-2 px-3 py-1.5 border border-[#77B9A0] rounded-lg bg-white hover:bg-gray-50 transition-colors text-xs"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" fill="white" stroke="#77B9A0" strokeWidth="1.5" />
                    <line x1="8" y1="7" x2="8" y2="20" stroke="#77B9A0" strokeWidth="1" opacity="0.3" />
                    <line x1="13" y1="7" x2="13" y2="20" stroke="#77B9A0" strokeWidth="1" opacity="0.3" />
                    <line x1="3" y1="11" x2="21" y2="11" stroke="#77B9A0" strokeWidth="1" opacity="0.3" />
                    <line x1="3" y1="15" x2="21" y2="15" stroke="#77B9A0" strokeWidth="1" opacity="0.3" />
                    <path d="M14 7L17 10L14 13M17 7L14 10L17 13" stroke="#77B9A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-xs font-medium text-gray-700">Download Template (.xlsx)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Section 2: Upload File */}
          <div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-[#77B9A0] text-white rounded-full flex items-center justify-center font-bold text-xs">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-0.5 text-xs">
                  Please upload filled Excel file (.xlsx only)
                </h3>
                <p className="text-[10px] text-gray-600 mb-1.5">
                  Upload the completed .xlsx file here.
                </p>

                {!uploadedFile ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-3 bg-white">
                    <div className="flex flex-col items-center justify-center space-y-1.5">
                      <div className="flex items-center justify-center">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 10H16.74C16.3659 8.551 15.5925 7.23599 14.5083 6.19999C13.4241 5.16399 12.0727 4.44999 10.6065 4.13699C9.1403 3.82399 7.61047 3.92399 6.18993 4.42599C4.7694 4.92799 3.50897 5.81399 2.54484 6.98799C1.58071 8.16199 0.949998 9.58199 0.719998 11.09C0.489998 12.598 0.667998 14.1399 1.23999 15.5599C1.81199 16.9799 2.75999 18.2199 3.97999 19.1399C5.19999 20.0599 6.63999 20.6199 8.14999 20.7499" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          <path d="M12 14V18M12 18L9 15M12 18L15 15" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <button
                        onClick={handleUpload}
                        className="px-3 py-1 border border-[#77B9A0] rounded-full bg-white text-[#77B9A0] hover:bg-[#DFFEEF] transition-colors text-xs font-medium"
                      >
                        Click to Upload (.xlsx)
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-[#77B9A0] rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-12 h-12 bg-[#DFFEEF] rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="3" width="18" height="18" rx="2" fill="white" stroke="#77B9A0" strokeWidth="1.5" />
                            <path d="M7 7H17M7 12H17M7 17H12" stroke="#77B9A0" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate">{uploadedFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(uploadedFile.size / 1024).toFixed(2)} KB
                          </p>
                          {uploading && (
                            <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                              <div
                                className="h-full bg-[#77B9A0] rounded-full transition-all duration-300 animate-pulse"
                                style={{ width: '75%' }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveFile}
                        disabled={uploading}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-2 disabled:opacity-50"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Save button at bottom right */}
        <div className="px-3 py-1.5 border-t border-gray-200 flex justify-end rounded-b-2xl bg-white">
          <button
            onClick={handleSave}
            disabled={!uploadedFile || uploading}
            className="px-6 py-1.5 bg-[#77B9A0] text-white rounded-lg hover:bg-[#5FA888] transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {uploading && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span>{uploading ? 'Uploading...' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkEmployeeUploadModal;